// SAB-605: single source of truth for money-action error copy + routing intent.
// SHARED across ALL money actions (deposits, paid-league joins, buy-sabotage,
// withdrawals), not deposits only, despite the file and function name.
//
// The deposit edge fns (paypal-create-order, aeropay-deposit, soap-create-checkout)
// return { error, code } with codes GEO_BLOCKED, KYC_REQUIRED, MIN_DEPOSIT,
// DEPOSIT_LIMIT, …, and the client's callEdgeFunction/callEdge attaches `.code`/`.body`
// to the thrown Error. This maps that to a human sentence + the action a surface
// should take, so a *working* geo block reads as a location message (not the old
// hardcoded "Payment failed. Please try again.", which hid the control and told an
// ineligible user to retry the impossible), and a KYC block routes to identity
// verification instead of a dead toast.

export type DepositErrorAction =
  | 'verify_identity'      // KYC_REQUIRED → launch the identity-verification flow
  | 'responsible_gaming'   // DEPOSIT_LIMIT → route to Responsible Gaming settings
  | 'add_funds'            // insufficient balance → route to add funds
  | null;

export interface MappedDepositError {
  title: string;
  message: string;
  action: DepositErrorAction;
}

// SAB-631: the SERVER forwards a block CATEGORY (+ the detected state on a jurisdiction
// block) so the toast can name the state and use the right retry treatment. We still never
// surface the raw compliance_reason (device/IP internals) — only the category label + state.
// Generic copy is the fallback when no category is present (client-enforce fail-close, or an
// edge fn not yet carrying the field during the deploy window) — today's behavior, unchanged.
const GEO_COPY =
  "Real-money contests aren't available in your location. You must be physically located in an eligible state.";
const GEO_VPN_COPY =
  'Turn off your VPN or proxy and try again — we need to confirm your real location for real-money play.';
const GEO_TECH_COPY =
  "We couldn't verify your location. Please try again.";

// Full US map: the detected state can be ANY state, not just our 19 eligible ones (a block
// names wherever the player actually is). Code → friendly name; anything not a recognized US
// state resolves to null → generic copy, so we never render a bogus 2-letter label.
const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'Washington, D.C.', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
  KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

function stateLabel(code: unknown): string | null {
  if (typeof code !== 'string') return null;
  return US_STATE_NAMES[code.trim().toUpperCase()] ?? null;
}

// SAB-631: category → copy. jurisdiction is terminal and names the state (no retry language);
// vpn_proxy and technical are retriable; an absent/unknown category degrades to the generic
// terminal jurisdiction copy so a half-deployed state never tells an eligible user to retry.
function mapGeoBlock(category: unknown, stateCode: unknown): MappedDepositError {
  switch (category) {
    case 'vpn_proxy':
      return { title: 'Turn off your VPN or proxy', message: GEO_VPN_COPY, action: null };
    case 'technical':
      return { title: 'Location check failed', message: GEO_TECH_COPY, action: null };
    case 'jurisdiction': {
      const label = stateLabel(stateCode);
      return {
        title: label ? `Not available in ${label}` : 'Not available in your location',
        message: label
          ? `Real-money contests aren't available in ${label}. You must be physically located in an eligible state.`
          : GEO_COPY,
        action: null,
      };
    }
    default:
      return { title: 'Not available in your location', message: GEO_COPY, action: null };
  }
}
const KYC_COPY =
  'Verify your identity to add funds — it only takes a few minutes.';
const INSUFFICIENT_COPY =
  "You don't have enough balance for this. Add funds and try again.";
const GENERIC_COPY = 'Payment failed. Please try again.';

/**
 * Map a thrown deposit error (or any error the payment SDK surfaces) to user-facing
 * copy + a routing action. Unknown / genuine payment failures fall through to the
 * retry message, which is the correct thing to say when retrying might actually work.
 */
export function mapDepositError(err: unknown): MappedDepositError {
  // category + detected_state arrive either at the top level (object-literal callers that pass
  // { code, message, category, detected_state } from the parsed body) or under `.body` (callers
  // that attach the parsed 403 JSON to the thrown Error). Read both so every surface behaves.
  const e = (err ?? {}) as { code?: string; message?: string; category?: unknown; detected_state?: unknown; body?: any };
  const code = e.code;
  const msg = typeof e.message === 'string' ? e.message : '';
  const body = e.body ?? {};
  const geoCategory = e.category ?? body.category ?? null;
  const geoState = e.detected_state ?? body.detected_state ?? null;

  switch (code) {
    case 'GEO_BLOCKED':
      return mapGeoBlock(geoCategory, geoState);
    case 'KYC_REQUIRED':
      return { title: 'Verify your identity', message: KYC_COPY, action: 'verify_identity' };
    case 'DEPOSIT_LIMIT':
      // edge returns a human, amount-aware message; keep it and route to RG.
      return { title: 'Deposit limit reached', message: msg || 'You have reached a deposit limit.', action: 'responsible_gaming' };
    case 'PURCHASE_LIMIT':
      // SAB-662: card-purchase spend limit. Amount-aware message from the edge; route to RG.
      return { title: 'Purchase limit reached', message: msg || 'You have reached a purchase limit.', action: 'responsible_gaming' };
    case 'RG_BLOCKED':
      // Responsible-gaming block (self-exclusion, cool-off, or a non-deposit limit).
      // The server sends a human, situation-specific reason; surface it as-is rather
      // than degrading it to the generic retry line.
      return { title: 'Account restriction', message: msg || 'This action is blocked by your responsible-gaming settings.', action: null };
    case 'MIN_DEPOSIT':
      return { title: 'Below the minimum', message: msg || 'That amount is below the minimum deposit.', action: null };
    case 'INSUFFICIENT_FUNDS':
    case 'INSUFFICIENT_BALANCE':
      return { title: 'Not enough balance', message: INSUFFICIENT_COPY, action: 'add_funds' };
  }

  // Codeless insufficient-balance (some paths return only the message string).
  if (/insufficient (balance|funds)/i.test(msg)) {
    return { title: 'Not enough balance', message: INSUFFICIENT_COPY, action: 'add_funds' };
  }

  // Genuine / unknown payment failure — retry is legitimate here. Never echo transport
  // noise ("...non-2xx status code", "Failed to fetch") at the user.
  return { title: 'Payment failed', message: GENERIC_COPY, action: null };
}
